import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useNavigate } from "react-router"
import { ArrowLeft } from "@untitledui/icons"
import { useOrganization } from "@/lib/organization/organization-context"
import { StepSend } from "./step-send"
import {
  useQuickEstimateState,
  type QuickEstimateStepIndex,
} from "./use-quick-estimate-state"
import { StepErrorBoundary } from "./error-boundary"
import { QE_WIZARD_COPY } from "./copy"
import { ConfirmLeave } from "../wizard-shared/confirm-leave"
import { StepVehicle } from "../wizard-shared/step-vehicle"
import { StepGuidedPhotos } from "./step-guided-photos"
import { StepDamageTag } from "./damage-tag/step-damage-tag"
import { NoTeamPicked } from "../wizard-shared/no-team-picked"
import { ProgressRail } from "../wizard-shared/progress-rail"
import { useEstimateCapture } from "../wizard-shared/use-estimate-capture"
import { useReturnUrl } from "../wizard-shared/use-return-url"

const STEP_LABELS = ["Vehicle", "Damage", "Photos", "Send"] as const

export function QuickEstimateWizard() {
  const { state, actions, hydrated } = useQuickEstimateState()
  const { activeTeamId } = useOrganization()
  const teamId = activeTeamId
  const navigate = useNavigate()
  const returnUrl = useReturnUrl()

  const { capture, cameraNodes } = useEstimateCapture(actions)

  // Hydrated step from sessionStorage. Until hydration completes,
  // render the same `step` we'd land on for a fresh session (0).
  const step: QuickEstimateStepIndex = hydrated ? state.step : 0

  // Confirm-leave overlay state. Service line completes in one
  // sitting at the lane; tab close or navigation kills the draft
  // (sessionStorage). The confirm sets that expectation honestly.
  const [confirmingLeave, setConfirmingLeave] = useState(false)

  if (!teamId) {
    return (
      <NoTeamPicked
        emoji="🛠️"
        title={QE_WIZARD_COPY.noTeamTitle}
        body={QE_WIZARD_COPY.noTeamBody}
        ctaLabel={QE_WIZARD_COPY.noTeamCta}
      />
    )
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-zinc-50 to-white">
      {/* Background dot grid (shared with QE2 for family identity). */}
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative max-w-[640px] mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Top exit + step counter */}
        <div className="flex items-center justify-between mb-5">
          <button
            type="button"
            onClick={() => setConfirmingLeave(true)}
            className="inline-flex items-center gap-1.5 h-9 -ml-1 pl-1.5 pr-3 rounded-lg text-[13px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-all duration-150"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            {QE_WIZARD_COPY.leaveLabel}
          </button>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
            {STEP_LABELS[step]}
          </span>
        </div>

        {/* Step card with progress rail */}
        <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
          <ProgressRail step={step} labels={STEP_LABELS} />
          <div className="p-5 md:p-10">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 18, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -18, scale: 0.985 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.9,
                  opacity: { duration: 0.25 },
                }}
              >
                {step === 0 && (
                  <StepErrorBoundary stepLabel="Vehicle">
                    <StepVehicle
                      vin={state.vin}
                      vehicle={state.vehicle}
                      onVin={actions.setVin}
                      onVehicle={actions.setVehicle}
                      onContinue={() => actions.setStep(1)}
                      eyebrowLabel="Step 1 of 4 · Vehicle"
                    />
                  </StepErrorBoundary>
                )}
                {step === 1 && (
                  <StepErrorBoundary stepLabel="Damage">
                    <StepDamageTag
                      tags={state.damageTags}
                      onTogglePanel={actions.toggleDamageTag}
                      onRemove={actions.removeDamageTag}
                      onUpdateTag={actions.updateDamageTag}
                      onContinue={() => actions.setStep(2)}
                      onBack={() => actions.setStep(0)}
                    />
                  </StepErrorBoundary>
                )}
                {step === 2 && (
                  <StepErrorBoundary stepLabel="Photos">
                    <StepGuidedPhotos
                      damageTags={state.damageTags}
                      photos={state.photos}
                      actions={actions}
                      capture={capture}
                      onBack={() => actions.setStep(1)}
                      onContinue={() => actions.setStep(3)}
                    />
                  </StepErrorBoundary>
                )}
                {step === 3 && (
                  <StepErrorBoundary stepLabel="Review">
                    <StepSend
                      vin={state.vin}
                      vehicle={state.vehicle}
                      photos={state.photos}
                      damageTags={state.damageTags}
                      notes={state.notes}
                      customer={state.customer}
                      actions={actions}
                      capture={capture}
                      teamId={teamId}
                      onBack={() => actions.setStep(2)}
                      eyebrowLabel="Step 4 of 4 · Send"
                    />
                  </StepErrorBoundary>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {cameraNodes}

      <ConfirmLeave
        open={confirmingLeave}
        title={QE_WIZARD_COPY.leaveConfirmTitle}
        body={QE_WIZARD_COPY.leaveConfirmBody}
        continueLabel={QE_WIZARD_COPY.leaveConfirmContinue}
        exitLabel={QE_WIZARD_COPY.leaveConfirmExit}
        onCancel={() => setConfirmingLeave(false)}
        onConfirm={() => navigate(returnUrl ?? "/dashboard")}
      />
    </div>
  )
}
